import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { listaGarantias } from '../../models/garantias';
import { useGarantias } from '../../hooks/useGarantias';
import { useColumnsGarantias } from '../../utils/columnsGarantias';
import { exportExcel } from '../../../../utils/exportExcel';
import DatosGarantiaForm from './datosGarantiasForm';
import SegurosGarantias from './segurosGarantias';
import { EnumTasacion, StatusCode, TipoBienRaiz, TipoFuncionalidad } from '../../../../utils/enums';
import { numericFormatter } from 'react-number-format';
import { Card, CardContent, Grid2, Typography } from '@mui/material';
import TextoConLabel from '../../../../components/inputs/genericos/materialui/textoConLabel';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import CustomTooltip from '../../../../components/ui/materialui/customToolTip';
import ErrorIcon from '@mui/icons-material/Error';
import TableHeader from '../../../../components/ui/materialui/tableHeader';
import Tabla from '../../../../components/ui/materialui/tabla';
import PropiedadDireccionForm from './propiedadDireccionForm';
import { copyGarantia } from '../../services/servicesGarantias';
import { useToast } from '../../../../hooks/useToast';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import { PermissionGuard } from '../../../../components/auth/ProtectedRoute';
import { useUserRolesContext } from '../../../../context/userRolesContext';
import { DetalleMutuoPermissionGuard } from '../../../../components/auth/DetalleMutuoPermissionGuard';

interface FormProps<T> {

    idMutuo: number;
    children?: ReactNode;
    isComplementario: boolean;
    idSolicitud: string | undefined;
    isDesactivado: boolean;
    esVigente?: boolean;
}

const GarantiaForm: React.FC<FormProps<any>> = ({ idMutuo, children, isComplementario, idSolicitud, isDesactivado, esVigente }) => {
    const {
        listaGarantias,
        listEstadoBienRaiz,
        hipotecas,
        defaultValues,
        defaultNuevo,
        obtenerLista,
        obtenerGarantias,
        setDefaultValues,
        listarSelectores
    } = useGarantias();

    const [show, setShow] = useState<boolean>(false);
    const [esNuevo, setEsNuevo] = useState<boolean>(false);
    const [valoresTotales, setValoresTotales] = useState({
        valorLiquidacion: 0,
        valorGarantia: 0,
        tasacion: 0,
        montoAsegurable: 0
    })
    const [errorTasacion, setErrorTasacion] = useState<string | undefined>(undefined);
    const { addToast } = useToast();
    const { hasPermission } = useUserRolesContext();
    const permisoCarteraMutuoEditarInfo = hasPermission(TipoFuncionalidad.Cartera_editar_info);
    const renderizar = async () => {
        await obtenerLista(idMutuo);
    }

    const { COLUMNS } = useColumnsGarantias(idMutuo, isDesactivado, obtenerGarantias, setShow, renderizar, isComplementario, esVigente);
    const columns = useMemo(() => COLUMNS, [listaGarantias, isDesactivado, esVigente]);

    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    useEffect(() => {
        obtenerLista(idMutuo);
        listarSelectores();
    }, [idMutuo]);


    useEffect(() => {
        if (listaGarantias.length) {
            const totalValorLiquidacion = listaGarantias.reduce((acc, garantia) => acc + garantia.valorLiquidacion, 0);
            const totalValorGarantia = listaGarantias.reduce((acc, garantia) => acc + garantia.valorGarantia, 0);
            const tasacion = (listaGarantias[0].montoMutuo / totalValorGarantia) * 100;

            const montoAsegurable = listaGarantias
                .filter((garantia: listaGarantias) => garantia.tipoBienRaizCodigo !== TipoBienRaiz.TERRENOS.toString()) // sacamos los terrenos para la suma
                .reduce((acc, garantia) => acc + garantia.valorGarantia, 0);

            setValoresTotales(prevState => ({
                ...prevState,
                montoAsegurable: montoAsegurable,
                tasacion: tasacion,
                valorLiquidacion: totalValorLiquidacion,
                valorGarantia: totalValorGarantia
            }));

            if (tasacion > EnumTasacion.ValorMaximoTasacion)
                setErrorTasacion(`La tasación no puede superar el ${EnumTasacion.ValorMaximoTasacion}% del valor de la garantía`)
            else
                setErrorTasacion(undefined)
        }
    }, [listaGarantias]);

    const handleNuevo = () => {
        setShow(true);
        setEsNuevo(true)
        setDefaultValues(defaultNuevo)
    }

    const onHide = () => {
        setShow(false);
        setEsNuevo(false)
    }

    return (
        <>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({
                        ...modalConfirmacion,
                        show: false,
                    })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />
            <Card sx={{ mb: 2, mt: 2 }} raised>
                <CardContent>
                    <PropiedadDireccionForm idMutuo={idMutuo} isComplementario={isComplementario} handleAfterCopy={renderizar} idSolicitud={idSolicitud} isDesactivado={isDesactivado} esVigente={esVigente} permisoCarteraMutuoEditarInfo={permisoCarteraMutuoEditarInfo} />
                </CardContent>
            </Card>
            <Card sx={{ mb: 2, mt: 2 }} raised>
                <CardContent>
                    <Typography className='card-title' variant="h6">Detalle tipo garantía</Typography>
                    <Grid2 container spacing={4} sx={{ mb: 2 }}>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <TextoConLabel
                                label={
                                    <>
                                        Total valor liquidación{" "}
                                        <CustomTooltip text="Es la suma de los valores de liquidación de todas las garantías.">
                                            <ErrorIcon />
                                        </CustomTooltip>
                                    </>
                                }
                                texto={numericFormatter(valoresTotales.valorLiquidacion.toFixed(4), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " })}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <TextoConLabel
                                label={
                                    <>
                                        Total valor garantía{" "}
                                        <CustomTooltip text="Es la suma del valor de todas las garantías.">
                                            <ErrorIcon />
                                        </CustomTooltip>
                                    </>
                                }
                                texto={numericFormatter(valoresTotales.valorGarantia.toFixed(4), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " })}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <TextoConLabel
                                label={
                                    <>
                                        Tasación{" "}
                                        <CustomTooltip text="Formula: (monto mutuo / total valor garantía) * 100">
                                            <ErrorIcon />
                                        </CustomTooltip>
                                    </>
                                }
                                texto={numericFormatter(valoresTotales.tasacion.toFixed(4), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "suffix": "%" })}
                                error={errorTasacion}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <TextoConLabel
                                label={
                                    <>
                                        Monto asegurable{" "}
                                        <CustomTooltip text="Es la suma de todas las garantías que no sean terrenos.">
                                            <ErrorIcon />
                                        </CustomTooltip>
                                    </>
                                }
                                texto={numericFormatter(valoresTotales.montoAsegurable.toFixed(4), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " })}
                            />
                        </Grid2>
                    </Grid2>
                    <Tabla
                        columns={columns}
                        data={listaGarantias}
                        titulo={
                            <TableHeader
                                button={!isComplementario ? (
                                    <DetalleMutuoPermissionGuard
                                        esVigente={esVigente}
                                        permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info]}>

                                        <MButton
                                            type="button"
                                            variant="contained"
                                            onClick={handleNuevo}
                                            disabled={isDesactivado}
                                            startIcon={<AddTwoToneIcon />}
                                        >
                                            Agregar
                                        </MButton>
                                    </DetalleMutuoPermissionGuard>
                                ) : undefined}
                                buttonExport={
                                    <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                    <MButton
                                        type="button"
                                        variant="outlined"
                                        startIcon={<SummarizeTwoToneIcon />}
                                        onClick={() => exportExcel(listaGarantias, columns, "Listado-Garantías")}
                                    >
                                        Exportar
                                    </MButton>
                                    </PermissionGuard>
                                }
                            />
                        }
                    />
                    <DatosGarantiaForm
                        idMutuo={idMutuo}
                        children={children}
                        listaGarantias={listaGarantias}
                        defaultValues={defaultValues}
                        show={show}
                        setShow={setShow}
                        onHide={onHide}
                        esNuevo={esNuevo}
                        renderizar={renderizar}
                        hipotecas={hipotecas}
                        listEstadoBienRaiz={listEstadoBienRaiz}
                        isComplementario={isComplementario}
                        isDesactivado={isDesactivado}
                        esVigente={esVigente}
                    />
                </CardContent>
            </Card>
            <Card sx={{ mb: 2, mt: 2 }} raised>
                <SegurosGarantias
                    idMutuo={idMutuo}
                    montoAsegurado={valoresTotales.montoAsegurable}
                    isComplementario={isComplementario}
                    isDesactivado={isDesactivado}
                    esVigente={esVigente!}
                />
            </Card>
        </>
    );
};

export default GarantiaForm;